/**
 * StateManager - Configuration management for Atmosphere effects
 * Handles reactive state updates and config merging
 */

import { PERFORMANCE_PROFILES, DEFAULT_CONFIG } from './Constants';
import type { PerformanceLevel } from './Benchmark';

export interface AtmosphereConfig {
  // General settings
  enabled: boolean;
  particleCount: number;
  
  // Snow specific
  snow: {
    enabled: boolean;
    intensity: number;        // 0-1, affects particle count multiplier
    speed: {
      min: number;
      max: number;
    };
    size: {
      min: number;
      max: number;
    };
    wind: {
      enabled: boolean;
      strength: number;       // Base wind strength
      frequency: number;      // Wind oscillation frequency
    };
    accumulation: {
      enabled: boolean;
      maxHeight: number;           // Maximum snow height in pixels
      rate: number;                // Accumulation rate multiplier
      meltRate: number;            // How fast snow melts (0 = never)
      groundRenderThrottle: number; // Frames between ground renders (lower = smoother, higher = faster)
    };
    color: {
      r: number;
      g: number;
      b: number;
      alpha: number;
    };
  };
}

// Re-export DEFAULT_CONFIG for consumers
export { DEFAULT_CONFIG } from './Constants';

type ConfigListener = (config: AtmosphereConfig) => void;

export class StateManager {
  private config: AtmosphereConfig;
  private listeners: Set<ConfigListener> = new Set();

  constructor(initialConfig?: Partial<AtmosphereConfig>) {
    this.config = this.mergeConfig(DEFAULT_CONFIG, initialConfig || {});
  }

  /**
   * Deep merge configuration objects (recursive for nested objects)
   * Note: For leaf objects like 'color', complete replacement is used
   *       to ensure color changes take effect immediately.
   */
  private mergeConfig(
    base: AtmosphereConfig,
    override: Partial<AtmosphereConfig>
  ): AtmosphereConfig {
    return this.deepMerge(
      base as unknown as Record<string, unknown>,
      override as unknown as Record<string, unknown>
    ) as unknown as AtmosphereConfig;
  }

  /**
   * Recursively deep merge two objects
   * Leaf objects (color, speed, size) are replaced entirely if provided
   */
  private deepMerge(base: Record<string, unknown>, override: Record<string, unknown>): Record<string, unknown> {
    const result = { ...base };
    
    // List of leaf objects that should be replaced entirely (not merged)
    const leafObjects = ['color', 'speed', 'size'];

    for (const key of Object.keys(override)) {
      const overrideValue = override[key];
      const baseValue = base[key];
      
      if (overrideValue === undefined) continue;

      // If it's a leaf object, replace entirely
      if (leafObjects.includes(key) && typeof overrideValue === 'object' && overrideValue !== null) {
        result[key] = { ...overrideValue };
      }
      // If both values are objects (and not arrays), recurse
      else if (
        typeof overrideValue === 'object' &&
        overrideValue !== null &&
        !Array.isArray(overrideValue) &&
        typeof baseValue === 'object' &&
        baseValue !== null &&
        !Array.isArray(baseValue)
      ) {
        result[key] = this.deepMerge(baseValue as Record<string, unknown>, overrideValue as Record<string, unknown>);
      } else {
        result[key] = overrideValue;
      }
    }

    return result;
  }

  /**
   * Get the current configuration
   */
  getConfig(): Readonly<AtmosphereConfig> {
    return this.config;
  }

  /**
   * Update configuration with partial values
   */
  updateConfig(updates: Partial<AtmosphereConfig>): void {
    this.config = this.mergeConfig(this.config, updates);
    this.notifyListeners();
  }

  /**
   * Subscribe to configuration changes
   */
  subscribe(listener: ConfigListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all subscribers of config change
   */
  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.config);
    }
  }

  /**
   * Reset to default configuration
   */
  reset(): void {
    this.config = { ...DEFAULT_CONFIG };
    this.notifyListeners();
  }

  /**
   * Apply a performance profile based on benchmark results
   */
  applyPerformanceProfile(level: PerformanceLevel): void {
    const profile = PERFORMANCE_PROFILES[level];
    
    this.updateConfig({
      particleCount: profile.particleCount,
      snow: {
        ...this.config.snow,
        accumulation: {
          ...this.config.snow.accumulation,
          rate: profile.accumulationRate,
          maxHeight: profile.maxAccumulationHeight,
          groundRenderThrottle: profile.groundRenderThrottle,
        },
      },
    });

    console.log(`[Atmosphere] Applied ${level} performance profile`);
  }
}

